import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface TransitionContextType {
  navigate: (path: string) => void;
  contentOpacity: number;
}

const TransitionContext = createContext<TransitionContextType | undefined>(undefined);

export const useTransition = () => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error("useTransition must be used within a TransitionProvider");
  }
  return context;
};

interface TransitionProviderProps {
  children: React.ReactNode;
}

export const TransitionProvider = ({ children }: TransitionProviderProps) => {
  const [contentOpacity, setContentOpacity] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  const handleNavigate = useCallback(
    async (path: string) => {
      if (location.pathname === path) return;

      isNavigatingRef.current = true;

      // Simple fade transition
      setContentOpacity(0);

      setTimeout(() => {
        navigate(path);
        setTimeout(() => {
          setContentOpacity(1);
          isNavigatingRef.current = false;
        }, 50);
      }, 200);
    },
    [location.pathname, navigate]
  );

  // Handle browser navigation (back/forward buttons)
  useEffect(() => {
    if (!isNavigatingRef.current) {
      setContentOpacity(0);
      setTimeout(() => setContentOpacity(1), 50);
    }
  }, [location.pathname]);

  const value: TransitionContextType = {
    navigate: handleNavigate,
    contentOpacity,
  };

  return <TransitionContext.Provider value={value}>{children}</TransitionContext.Provider>;
};
